import { useCallback, useMemo, type ChangeEvent, useId, useState, useRef, useEffect, forwardRef } from 'react';
import { loadAllProjectNames } from '../database/projectSuggestions';

export interface ProjectInputProps {
  name: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  disabled?: boolean;
  error?: string | null;
  helpText?: string;
  suggestions?: readonly string[];
}

export const ProjectInput = forwardRef<HTMLInputElement, ProjectInputProps>(function ProjectInput({ name, onChange, onBlur, id, disabled = false, error, helpText, suggestions }, ref) {
  const autoId = useId();
  const inputId = id ?? name ?? `project-input-${autoId}`;
  const hasError = !!error;
  const [loadedSuggestions, setLoadedSuggestions] = useState<string[]>([]);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);

  const projectOptions = useMemo(() => {
    const projects = suggestions?.length ? [...suggestions] : loadedSuggestions.length ? loadedSuggestions : ['Default'];
    const hasDefault = projects.includes('Default');
    return (hasDefault ? projects : ['Default', ...projects]).sort((a, b) => a.localeCompare(b));
  }, [loadedSuggestions, suggestions]);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    if (suggestions || hasLoadedSuggestions) return;

    let cancelled = false;
    loadAllProjectNames()
      .then(names => {
        if (!cancelled) {
          setLoadedSuggestions(names);
          setHasLoadedSuggestions(true);
        }
      })
      .catch(err => {
        console.warn('Failed to load project suggestions:', err);
        if (!cancelled) setHasLoadedSuggestions(true);
      });

    return () => {
      cancelled = true;
    };
  }, [hasLoadedSuggestions, suggestions]);

  const filtered = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return projectOptions;
    return projectOptions.filter(p => p.toLowerCase().includes(q));
  }, [projectOptions, inputValue]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setOpen(true);
      onChange?.(e);
    },
    [onChange]
  );

  const pick = useCallback(
    (p: string) => {
      setInputValue(p);
      setOpen(false);
      // Create a synthetic event to trigger RHF's onChange
      const syntheticEvent = {
        target: { value: p, name },
        currentTarget: { value: p, name }
      } as ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    },
    [name, onChange]
  );

  return (
    <div className="relative" ref={wrapRef}>
      <div className="group grid w-full grid-cols-[3fr_2fr_1fr_4fr] gap-x-3 rounded-lg px-2 py-px transition-all h-max-20 bg-(--bg-color)">
        <label htmlFor={inputId} className="flex items-center text-sm font-medium" style={{ color: hasError ? 'var(--text-color-error)' : 'var(--text-color)' }}>
          Project Name
        </label>

        <span className="flex items-center justify-center text-sm opacity-70" style={{ color: 'var(--text-color)' }} />
        <div />

        <div className="relative flex items-center">
          <input
            id={inputId}
            name={name}
            ref={ref}
            type="text"
            onChange={handleChange}
            onFocus={() => setOpen(true)}
            onBlur={e => {
              setOpen(false);
              onBlur?.(e);
            }}
            placeholder="Type or select project..."
            disabled={disabled}
            className="w-full rounded-md border px-3 py-2 text-sm transition-all placeholder:opacity-50 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: disabled ? 'rgba(0,0,0,0.03)' : 'white',
              color: hasError ? 'var(--text-color-error)' : 'var(--text-color)',
              borderColor: hasError ? 'var(--text-color-error)' : 'rgba(0,0,0,0.15)',
              boxShadow: hasError ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none'
            }}
          />

          {/* ✅ real dropdown */}
          {open && filtered.length > 0 && (
            <div className="absolute left-0 top-full z-50  mt-1 w-full max-h-56 overflow-auto rounded-md border bg-white shadow-lg" style={{ borderColor: 'rgba(0,0,0,0.15)' }}>
              {filtered.map(p => (
                <button
                  key={p}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-black/5"
                  onMouseDown={e => e.preventDefault()} // keeps input from losing focus before click
                  onClick={() => pick(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {hasError && (
            <div className="pointer-events-none absolute left-full top-1/2 z-1000 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium text-white shadow-lg" style={{ backgroundColor: 'var(--text-color-error)' }}>
              {error}
            </div>
          )}
        </div>

        {helpText ? (
          <div className="pointer-events-none absolute left-0 top-full z-1000 mt-2 hidden min-w-50 max-w-75 rounded-lg p-3 text-sm leading-relaxed shadow-xl group-hover:block group-focus-within:block" style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', border: '1px solid rgba(0,0,0,0.1)' }}>
            {helpText}
          </div>
        ) : null}
      </div>
    </div>
  );
});
